import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom';

// mui
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import { Drawer, FormControlLabel, InputAdornment, TextField, MenuItem } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';

// redux
import { useAppDispatch, useAppSelector } from '../../hooks'
import { createLatentInvitationCode, getFundraisingAmbassadors, getLatentOrganizationCodes, removeFundraisingAmbassador } from '../../managers/redux/slices/fundraising'
import { reloadAuthAccount } from '../../managers/redux/slices/authAccount'
import InviteTeamMember from '../../components/dialogs/team/InviteTeamMember'
import { COLORS } from '../../theme/muiTheme';

import { FundraisingCampaign, FundraisingCampaignAmbassador, FundraisingCampaignAmbassadorStatus, FundraisingProfile, LatentOrganizationCode } from '../../API'
import InviteAmbassador from '../../components/dialogs/fundraising/InviteAmbassador';
import ProfileAvatar from '../../components/data-display/ProfileAvatar';
import { Close } from '@mui/icons-material';
import { API } from 'aws-amplify';
import { listFundraisingCampaignAmbassadorsByFundraisingProfile } from '../../managers/graphql/queries';
import { deleteLatentOrganizationCode, updateLatentOrganizationCode } from '../../managers/graphql/mutations';
import { setLoading } from '../../managers/redux/slices/loading';
import FundraisingCampaignSelector from '../../components/inputs/FundraisingCampaignSelector';

export default function InvitationCodes(props: any) {

    const history = useHistory()

    // redux
    const dispatch = useAppDispatch()
    const fundraising = useAppSelector(state => state.fundraising)
    const authAccount = useAppSelector(state => state.authAccount)
    const selectedStaffProfile = useAppSelector(state => state.authAccount.selectedStaffProfile)

    // drawer
    const [showCodeDrawer, setShowCodeDrawer] = useState<boolean>(false)
    const [selectedCodeDrawerCode, setSelectedCodeDrawerCode] = useState<LatentOrganizationCode | null>(null)

    const [code, setCode] = useState<string | null>(null)
    const [active, setActive] = useState<boolean>(false)
    const [emailRestricted, setEmailRestricted] = useState<boolean>(false)
    const [emailHostRestriction, setEmailHostRestriction] = useState<string | null>(null)
    const [fundraisingCampaign, setFundraisingCampaign] = useState<FundraisingCampaign | null>(null)
    const [presetFundraisingCampaignAmbassadorStatus, setPresetFundraisingCampaignAmbassadorStatus] = useState<FundraisingCampaignAmbassadorStatus>(FundraisingCampaignAmbassadorStatus.Connected)

    useEffect(() => {
        (async () => {
            if (authAccount.cognitoData) {
                if (fundraising.latentOrganizationCodes == null) {
                    dispatch(getLatentOrganizationCodes())
                }
            } else {
                await dispatch(reloadAuthAccount())
                if (fundraising.latentOrganizationCodes == null) {
                    dispatch(getLatentOrganizationCodes())
                }
            }
        })()
    }, [])

    function handleDrawerClose() {
        setShowCodeDrawer(false)
        setSelectedCodeDrawerCode(null)
        setCode('')
        setActive(false)
        setEmailRestricted(false)
        setEmailHostRestriction(null)
    }

    async function handleDrawerOpen(latentOrganizationCode: LatentOrganizationCode) {
        setSelectedCodeDrawerCode(latentOrganizationCode)
        setShowCodeDrawer(true)
    }

    return (
        <div className="p-5 d-flex flex-column justify-content-start row">
            <div className="col-12 mb-4 g-0">
                <Button variant="contained" onClick={() => history.push('/admin/ambassadors')} className="m-0">Back to Ambassadors</Button>
            </div>
            <div className="col-12 border rounded bg-white mb-4 p-5">
                <div className="d-flex flex-row justify-content-between align-items-center">
                    <div className="d-flex flex-column justify-content-center align-items-start">
                        <Typography variant="h4">About Invitation Codes</Typography>
                        <Typography variant="body1">Invitation codes are created by your organization for use by multiple ambassadors. If you specify a Campaign when creating an invitation code then any ambassador that uses that code will automatically be added to your Organization and the Campaign you've selected. You can also restrict certain invitation codes to only be used by people with a specific email address. For example, if you only want people that work for your organization to have access to a specific invitation code then you can specify {`<your-email-domain>.com`} and only users with that email address ending can use the invitation code.</Typography>
                    </div>
                </div>
            </div>
            <div className="d-flex flex-row justify-content-between w-100 mb-3">
                <Typography variant="h3">{fundraising && fundraising.latentOrganizationCodes ? fundraising.latentOrganizationCodes.length : ''} Codes</Typography>
                <Button
                    onClick={() => {
                        setShowCodeDrawer(true)
                    }}
                >
                    Create New Code
                </Button>
            </div>

            <TableContainer component={Paper} className="p-0">
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">Code</TableCell>
                            <TableCell align="left">Email Restricted</TableCell>
                            <TableCell align="left">Email Host</TableCell>
                            <TableCell align="left">Campaign</TableCell>
                            <TableCell align="left">Active</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            fundraising && fundraising.latentOrganizationCodes && fundraising.latentOrganizationCodes.length > 0 ?
                                fundraising.latentOrganizationCodes.map((latentOrganizationCode: LatentOrganizationCode) => {
                                    if (latentOrganizationCode) {
                                        return (
                                            <TableRow
                                                key={latentOrganizationCode.id}
                                                sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer', ':hover': { backgroundColor: COLORS.ICE } }}
                                                onClick={() => { handleDrawerOpen(latentOrganizationCode) }}
                                            >
                                                <TableCell align="left">
                                                    {latentOrganizationCode.code}
                                                </TableCell>
                                                <TableCell align="left">
                                                    <Switch disabled checked={latentOrganizationCode.emailRestricted} />
                                                </TableCell>
                                                <TableCell align="left">
                                                    {latentOrganizationCode.emailHostRestriction ? latentOrganizationCode.emailHostRestriction : ''}
                                                </TableCell>
                                                <TableCell align="left">
                                                    {latentOrganizationCode.fundraisingCampaign && latentOrganizationCode.fundraisingCampaign.campaignName ? latentOrganizationCode.fundraisingCampaign.campaignName : ''}
                                                </TableCell>
                                                <TableCell align="left">
                                                    <Switch checked={latentOrganizationCode.active} />
                                                </TableCell>
                                            </TableRow>
                                        )
                                    } else {
                                        return <div></div>
                                    }
                                })
                                :
                                <div></div>
                        }
                    </TableBody>
                </Table>
            </TableContainer>

            <Drawer
                open={showCodeDrawer}
                onClose={handleDrawerClose}
                anchor="right"
                sx={{ maxWidth: '600px', zIndex: '10' }}
                style={{ zIndex: '10' }}
                variant="persistent"
            >
                {
                    selectedCodeDrawerCode ?
                        <div className='bg-light p-5 row w-100 mt-5' style={{ maxWidth: '600px', zIndex: '10' }}>
                            <div className="col-12 d-flex flex-row justify-content-between align-items-center w-100">
                                <Typography variant="h4">Manage Invitation Code</Typography>
                                <IconButton onClick={handleDrawerClose}>
                                    <Close sx={{ color: 'black' }} />
                                </IconButton>
                            </div>
                            <div className="col-12 d-flex flex-column justify-content-center align-items-start my-3">
                                <FormControlLabel label="Active" control={<Switch checked={selectedCodeDrawerCode.active} onChange={async (e, checked) => {
                                    const _selectedCodeDrawerCode: LatentOrganizationCode = JSON.parse(JSON.stringify(selectedCodeDrawerCode))
                                    _selectedCodeDrawerCode.active = checked
                                    setSelectedCodeDrawerCode(_selectedCodeDrawerCode)
                                    dispatch(setLoading({ loading: true, message: 'Removing invitation code...' }))
                                    const { data }: any = await API.graphql({
                                        query: updateLatentOrganizationCode,
                                        variables: {
                                            input: {
                                                id: selectedCodeDrawerCode.id,
                                                active: checked
                                            }
                                        }
                                    })
                                    await dispatch(getLatentOrganizationCodes())
                                    dispatch(setLoading({ loading: false, message: '' }))
                                }} />} />
                            </div>
                            <div className="col-12 d-flex flex-column justify-content-center align-items-center my-3">
                                <TextField InputLabelProps={{ shrink: true }} label="Code" disabled value={selectedCodeDrawerCode.code} variant="outlined" fullWidth />
                            </div>
                            <div className="col-12 d-flex flex-column justify-content-center align-items-start my-3">
                                <FormControlLabel label="Email Restricted" disabled control={<Switch checked={selectedCodeDrawerCode.emailRestricted} />} />
                            </div>
                            {
                                selectedCodeDrawerCode.emailRestricted &&
                                <div className="col-12 d-flex flex-column justify-content-center align-items-center w-100 my-3">
                                    <TextField disabled label="Email Host" helperText="Examples: gmail.com, onflourish.com, <your organization's website>.com" onChange={(e) => setEmailHostRestriction(e.target.value)} value={selectedCodeDrawerCode.emailHostRestriction} variant="outlined" fullWidth InputProps={{ startAdornment: <InputAdornment position='start'>@</InputAdornment> }} />
                                </div>
                            }
                            <div className="col-12 d-flex flex-column justify-content-center align-items-center my-3">
                                <FundraisingCampaignSelector disabled value={fundraisingCampaign} onChange={(_fundraisingCampaign) => setFundraisingCampaign(_fundraisingCampaign)} label="Fundraising Campaign" />
                            </div>
                            {
                                authAccount.data && authAccount.data.flourishAdmin && selectedCodeDrawerCode.fundraisingCampaign &&
                                <div className="col-12 d-flex flex-column justify-content-center align-items-start my-3">
                                    <div className='mb-3 w-100' style={{ height: '0.5px', backgroundColor: 'gray' }} />
                                    <Typography variant="h5" className="mb-3">Flourish Admin Options</Typography>
                                    <Typography>When the code is entered users should</Typography>
                                    <Select
                                        disabled
                                        value={selectedCodeDrawerCode.presetFundraisingCampaignAmbassadorStatus ? selectedCodeDrawerCode.presetFundraisingCampaignAmbassadorStatus : FundraisingCampaignAmbassadorStatus.Connected}
                                        label=" "
                                        fullWidth
                                        onChange={(e) => setPresetFundraisingCampaignAmbassadorStatus(e.target.value as FundraisingCampaignAmbassadorStatus)}
                                        defaultValue={FundraisingCampaignAmbassadorStatus.Connected}
                                    >
                                        <MenuItem value={FundraisingCampaignAmbassadorStatus.Connected}>be connected to the Campaign</MenuItem>
                                        <MenuItem value={FundraisingCampaignAmbassadorStatus.Waiting}>be asked to join the Campaign</MenuItem>
                                        <MenuItem value={FundraisingCampaignAmbassadorStatus.Accepted}>be added to the Campaign immediately</MenuItem>
                                    </Select>
                                </div>
                            }
                            <div className="col-12 d-flex flex-column justify-content-center align-items-center my-3">
                                <Button fullWidth variant="outlined" color="error" onClick={async () => {
                                    // TODO delete invitation code
                                    dispatch(setLoading({ loading: true, message: 'Removing invitation code...' }))
                                    const { data }: any = await API.graphql({
                                        query: deleteLatentOrganizationCode,
                                        variables: {
                                            input: {
                                                id: selectedCodeDrawerCode.id
                                            }
                                        }
                                    })
                                    await dispatch(getLatentOrganizationCodes())
                                    handleDrawerClose()
                                    dispatch(setLoading({ loading: false, message: '' }))
                                }}>Delete Code</Button>
                            </div>
                        </div>
                        :
                        <div className='bg-light p-5 row w-100 mt-5' style={{ maxWidth: '600px', zIndex: '10' }}>
                            <div className="col-12 d-flex flex-row justify-content-between align-items-center w-100">
                                <Typography variant="h4">Add a new Invitation Code</Typography>
                                <IconButton onClick={handleDrawerClose}>
                                    <Close sx={{ color: 'black' }} />
                                </IconButton>
                            </div>
                            <div className="col-12 d-flex flex-column justify-content-center align-items-start my-3">
                                <FormControlLabel label="Active" control={<Switch checked={active} onChange={(e, checked) => setActive(checked)} />} />
                            </div>
                            <div className="col-12 d-flex flex-column justify-content-center align-items-center my-3">
                                <TextField label="Code" onChange={(e) => setCode(e.target.value)} value={code} variant="outlined" fullWidth />
                            </div>
                            <div className="col-12 d-flex flex-column justify-content-center align-items-start my-3">
                                <FormControlLabel label="Email Restricted" control={<Switch checked={emailRestricted} onChange={(e, checked) => setEmailRestricted(checked)} />} />
                            </div>
                            {
                                emailRestricted &&
                                <div className="col-12 d-flex flex-column justify-content-center align-items-center w-100 my-3">
                                    <TextField label="Email Host" helperText="Examples: gmail.com, onflourish.com, <your organization's website>.com" onChange={(e) => setEmailHostRestriction(e.target.value)} value={emailHostRestriction} variant="outlined" fullWidth InputProps={{ startAdornment: <InputAdornment position='start'>@</InputAdornment> }} />
                                </div>
                            }
                            <div className="col-12 d-flex flex-column justify-content-center align-items-center my-3">
                                <FundraisingCampaignSelector value={fundraisingCampaign} onChange={(_fundraisingCampaign) => setFundraisingCampaign(_fundraisingCampaign)} label="Fundraising Campaign" />
                            </div>
                            {
                                authAccount.data && authAccount.data.flourishAdmin && fundraisingCampaign &&
                                <div className="col-12 d-flex flex-column justify-content-center align-items-start my-3">
                                    <div className='mb-3 w-100' style={{ height: '0.5px', backgroundColor: 'gray' }} />
                                    <Typography variant="h5" className="mb-3">Flourish Admin Options</Typography>
                                    <Typography>When the code is entered users should</Typography>
                                    <Select
                                        value={presetFundraisingCampaignAmbassadorStatus}
                                        label=" "
                                        fullWidth
                                        onChange={(e) => setPresetFundraisingCampaignAmbassadorStatus(e.target.value as FundraisingCampaignAmbassadorStatus)}
                                        defaultValue={FundraisingCampaignAmbassadorStatus.Connected}
                                    >
                                        <MenuItem value={FundraisingCampaignAmbassadorStatus.Connected}>be connected to the Campaign (default)</MenuItem>
                                        <MenuItem value={FundraisingCampaignAmbassadorStatus.Waiting}>be asked to join the Campaign</MenuItem>
                                        <MenuItem value={FundraisingCampaignAmbassadorStatus.Accepted}>be added to the Campaign immediately</MenuItem>
                                    </Select>
                                </div>
                            }
                            <div className="col-12 d-flex flex-column justify-content-center align-items-center my-3">
                                <Button fullWidth variant="contained" onClick={async () => {
                                    await dispatch(createLatentInvitationCode({
                                        code,
                                        active,
                                        emailRestricted,
                                        emailHostRestriction,
                                        fundraisingCampaignID: fundraisingCampaign ? fundraisingCampaign.id : null,
                                        presetFundraisingCampaignAmbassadorStatus
                                    }))
                                    handleDrawerClose()
                                }}>Save</Button>
                            </div>
                        </div>
                }
            </Drawer>
        </div>
    )
}